/**
 * The Jacobian of the residual system — J[i][j] = ∂residualᵢ/∂xⱼ.
 *
 * Newton solves J·Δ = −F each iteration, so the Jacobian is the heart of the
 * solve. This builds it two ways, from the same `RNode` residual graphs:
 *   - **symbolic** — differentiate each residual w.r.t. each variable it touches,
 *     as another `RNode` (rendered to text for the panel);
 *   - **numeric** — its value at a point, which is just the gradient `evalJet`
 *     already computes (one Jacobian row per residual).
 *
 * It's sparse: a residual only mentions the variables at its own bus and the
 * buses one branch away, so most (i,j) entries are structurally zero and never
 * appear here.
 */

import { RNode, GroundedResidual, evalJet, varsOf, rnodeToString } from './residual';
import { GroundedResidualSystem } from '../groundedProblem';

const ZERO: RNode = { k: 'const', v: 0 };
const ONE: RNode = { k: 'const', v: 1 };
const isZero = (n: RNode): boolean => n.k === 'const' && n.v === 0;
const isOne = (n: RNode): boolean => n.k === 'const' && n.v === 1;

// Simplifying constructors — fold the 0s and 1s that differentiation throws off,
// so a symbolic partial reads as `Gff(l) * ...` rather than `0 + 1 * (...) + 0`.
const sNeg = (a: RNode): RNode => (isZero(a) ? ZERO : { k: 'neg', x: a });
const sAdd = (a: RNode, b: RNode): RNode => (isZero(a) ? b : isZero(b) ? a : { k: 'bin', op: '+', l: a, r: b });
const sSub = (a: RNode, b: RNode): RNode =>
  isZero(b) ? a : isZero(a) ? sNeg(b) : { k: 'bin', op: '-', l: a, r: b };
const sMul = (a: RNode, b: RNode): RNode =>
  isZero(a) || isZero(b) ? ZERO : isOne(a) ? b : isOne(b) ? a : { k: 'bin', op: '*', l: a, r: b };
const sDiv = (a: RNode, b: RNode): RNode => (isZero(a) ? ZERO : { k: 'bin', op: '/', l: a, r: b });

/** The symbolic derivative of a residual node w.r.t. one variable. */
export function differentiate(node: RNode, v: string): RNode {
  switch (node.k) {
    case 'const':
      return ZERO;
    case 'var':
      return node.name === v ? ONE : ZERO;
    case 'neg':
      return sNeg(differentiate(node.x, v));
    case 'bin': {
      const { l, r, op } = node;
      const dl = differentiate(l, v);
      const dr = differentiate(r, v);
      switch (op) {
        case '+':
          return sAdd(dl, dr);
        case '-':
          return sSub(dl, dr);
        case '*':
          // (uv)' = u'v + uv'
          return sAdd(sMul(dl, r), sMul(l, dr));
        case '/':
          // (u/v)' = (u'v − uv') / v²
          return sDiv(sSub(sMul(dl, r), sMul(l, dr)), sMul(r, r));
      }
    }
    // eslint-disable-next-line no-fallthrough
    case 'fn': {
      const da = differentiate(node.x, v);
      if (isZero(da)) return ZERO; // chain rule: nothing to propagate
      const a = node.x;
      if (node.fn === 'sqrt') return sDiv(da, sMul({ k: 'const', v: 2 }, { k: 'fn', fn: 'sqrt', x: a }));
      if (node.fn === 'sin') return sMul({ k: 'fn', fn: 'cos', x: a }, da);
      // cos
      return sMul(sNeg({ k: 'fn', fn: 'sin', x: a }), da);
    }
  }
}

/** One nonzero Jacobian entry: its position, symbolic partial, and value at a point. */
export interface JacobianEntry {
  row: number;
  col: number;
  rowName: string;
  colName: string;
  /** ∂residual/∂variable, symbolically. */
  symbolic: string;
  /** Its value at the supplied point, if one was given. */
  value?: number;
}

export interface Jacobian {
  /** Residual (row) names, in grounding order. */
  rows: string[];
  /** Variable (column) names, in first-appearance order. */
  cols: string[];
  /** The structurally-nonzero entries, sorted by (row, col). */
  entries: JacobianEntry[];
}

/**
 * Build the sparse Jacobian of a residual system. With `point` given, each entry
 * also carries its numeric value there (from `evalJet`'s gradient); without it,
 * only the symbolic form.
 */
export function buildJacobian(
  system: GroundedResidualSystem,
  point?: ReadonlyMap<string, number>,
): Jacobian {
  const rows = system.residuals.map((r) => r.name);
  const cols = system.variables;
  const colIndex = new Map(cols.map((c, i) => [c, i]));

  const entries: JacobianEntry[] = [];
  system.residuals.forEach((res: GroundedResidual, row) => {
    const jet = point ? evalJet(res.node, point) : undefined;
    for (const v of varsOf(res.node)) {
      const deriv = differentiate(res.node, v);
      if (isZero(deriv)) continue; // a variable that cancels out (rare)
      entries.push({
        row,
        col: colIndex.get(v) ?? -1,
        rowName: res.name,
        colName: v,
        symbolic: rnodeToString(deriv),
        value: jet?.grad.get(v),
      });
    }
  });

  entries.sort((a, b) => a.row - b.row || a.col - b.col);
  return { rows, cols, entries };
}
